import React, { useEffect, useRef, useState } from 'react';
import { ExternalLinkIcon } from './ActionIcons';

export interface LogFilterOption {
    id: string;
    label: string;
    url: string;
}

interface LogFilterMenuProps {
    logs: LogFilterOption[];
    /** Log shown on its own, or null for all logs. */
    selectedId: string | null;
    /** Logs covered by the current view when it is narrowed some other way (e.g. a raid night). */
    highlightedIds?: string[];
    onSelect: (id: string | null) => void;
}

/** Search only pays off once the list is long. */
const SEARCH_THRESHOLD = 6;

const LogLink: React.FC<{ log: LogFilterOption }> = ({ log }) => (
    <a
        className="log-menu-link"
        href={log.url}
        target="_blank"
        rel="noreferrer"
        title={`Open ${log.label} on Warcraft Logs`}
        aria-label={`Open ${log.label} on Warcraft Logs`}
    >
        <ExternalLinkIcon />
    </a>
);

/** Compact log filter: one dropdown instead of a chip per log, with a Warcraft Logs link per log. */
const LogFilterMenu: React.FC<LogFilterMenuProps> = ({ logs, selectedId, highlightedIds = [], onSelect }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        searchRef.current?.focus();
        const handlePointerDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    if (logs.length === 1) {
        return (
            <span className="log-menu-single">
                {logs[0].label}
                <LogLink log={logs[0]} />
            </span>
        );
    }

    const selected = logs.find((log) => log.id === selectedId) ?? null;
    const highlighted = new Set(highlightedIds);
    const buttonLabel = selected
        ? selected.label
        : highlighted.size > 0
          ? `${highlighted.size} of ${logs.length} logs`
          : `All logs (${logs.length})`;
    const needle = query.trim().toLowerCase();
    const visibleLogs = needle ? logs.filter((log) => log.label.toLowerCase().includes(needle)) : logs;

    const choose = (id: string | null) => {
        onSelect(id);
        setOpen(false);
        setQuery('');
    };

    return (
        <div className="log-menu" ref={rootRef}>
            <button
                type="button"
                className={`log-menu-button${selected ? ' is-active' : ''}${open ? ' is-open' : ''}`}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
            >
                {buttonLabel}
                <span className="app-tab-caret" aria-hidden="true">
                    ▾
                </span>
            </button>
            {selected && <LogLink log={selected} />}
            {open && (
                <div className="log-menu-panel">
                    {logs.length > SEARCH_THRESHOLD && (
                        <input
                            ref={searchRef}
                            className="log-menu-search"
                            type="search"
                            placeholder="Search logs…"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                    )}
                    <div className="log-menu-list" role="listbox" aria-label="Warcraft Logs reports">
                        {!needle && (
                            <div className={`log-menu-item${selectedId === null ? ' is-selected' : ''}`}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={selectedId === null}
                                    onClick={() => choose(null)}
                                >
                                    All logs
                                </button>
                            </div>
                        )}
                        {visibleLogs.map((log) => (
                            <div
                                key={log.id}
                                className={`log-menu-item${log.id === selectedId ? ' is-selected' : ''}${
                                    highlighted.has(log.id) ? ' is-highlighted' : ''
                                }`}
                            >
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={log.id === selectedId}
                                    onClick={() => choose(log.id)}
                                >
                                    {log.label}
                                </button>
                                <LogLink log={log} />
                            </div>
                        ))}
                        {visibleLogs.length === 0 && <p className="log-menu-empty">No logs match “{query}”.</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LogFilterMenu;
